/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo } from 'react';
import { useLocation, useParams } from 'wouter';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { FontFaces, FormPanel, Link } from '@micromag/core/components';
import { useUrlGenerator } from '@micromag/core/contexts';
import { PropTypes as MicromagPropTypes } from '@micromag/core';
import { useParsedStory } from '@micromag/core/hooks';
import { ScreensProvider } from '@micromag/screens';

import { useStory } from '../../../hooks';
import MainLayout from '../../layouts/Main';
import Page from '../../partials/Page';
import ScreensList from '../../lists/Screens';
import StoryMenu from '../../menus/Story';

const propTypes = {
    className: PropTypes.string,
};

const defaultProps = {
    className: null,
};

const ParticipationPage = ({ className }) => {
    const [pathname] = useLocation();
    const url = useUrlGenerator();
    const { story: storyId } = useParams();
    const { story } = useStory(storyId);
    const { components: screens = [], fonts = null } = useParsedStory(story) || {};
    const finalFonts = useMemo(() => fonts || [], [fonts]);

    const contributionScreens =
        screens && screens.length > 0 ? screens.filter((s) => s.type === 'contribution') : null;

    const quizScreens =
        screens && screens.length > 0 ? screens.filter((s) => s.type === 'quiz') : null;

    const surveyScreens =
        screens && screens.length > 0 ? screens.filter((s) => s.type === 'survey') : null;

    const parent = story !== null ? story.title : null;
    const parentUrl = story !== null ? url('stories.show', { story: story.id }) : null;

    const title = <FormattedMessage defaultMessage="Participation" descrition="Page title" />;
    const nav = [
        { label: parent, url: parentUrl },
        { label: title, url: pathname },
    ];

    const hasContrib = contributionScreens !== null && contributionScreens.length > 0;
    const hasQuiz = quizScreens !== null && quizScreens.length > 0;
    const hasSurvey = surveyScreens !== null && surveyScreens.length > 0;

    return (
        <MainLayout nav={nav}>
            <Page
                title={title}
                section={
                    <Link href={parentUrl} withoutStyle>
                        {parent}
                    </Link>
                }
                sidebar={story !== null ? <StoryMenu story={story} asList /> : <div />}
                className={classNames([
                    {
                        [className]: className !== null,
                    },
                ])}
            >
                <FontFaces fonts={finalFonts} />
                <ScreensProvider>
                    <FormPanel>
                        {hasContrib ? (
                            <div className="mb-2">
                                <ScreensList
                                    items={contributionScreens}
                                    story={story}
                                    itemLink="stories.contributions.show"
                                    itemType="list"
                                />
                            </div>
                        ) : null}
                        {hasQuiz ? (
                            <div className="mb-2">
                                <ScreensList
                                    items={quizScreens}
                                    story={story}
                                    itemLink="stories.quiz.show"
                                    itemType="list"
                                />
                            </div>
                        ) : null}
                        {hasSurvey ? (
                            <div className="mb-2">
                                <ScreensList
                                    items={surveyScreens}
                                    story={story}
                                    itemLink="stories.survey.show"
                                    itemType="list"
                                />
                            </div>
                        ) : null}
                        {!hasContrib && !hasQuiz ? (
                            <FormattedMessage
                                defaultMessage="Add a Quiz or Contribution screen to your Micromag to see the results."
                                descrition="Empty message"
                            />
                        ) : null}
                    </FormPanel>
                </ScreensProvider>
            </Page>
        </MainLayout>
    );
};

ParticipationPage.propTypes = propTypes;
ParticipationPage.defaultProps = defaultProps;

export default ParticipationPage;
